import React, { useEffect, useState, useContext, useRef } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { getQuoteRequests, getRfqDuplicates } from "api/QuoteRequests";
import { trackActivity } from "api/ActivityTracker";
import TableData from "components/ui/TableData";
import { Badge, Box, Flex, Image } from "theme-ui";
import moment from "moment-timezone";
import Span from "components/typography/Span";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { EllipsisIcon } from "components/svg";
import UserContext from "context/User";
import { themevals } from "theme/themevals";
import BasicModal from "components/ui/BasicModal";
import { QuestionMarkIcon } from "components/svg";
import BasicButton from "components/forms/BasicButton";
import { TrashIcon } from "components/svg";
import P from "components/typography/P";
import { updateQuoteRequest } from "api/QuoteRequests";
import { reviveRfq } from "api/QuoteExpiration";
import { useToast } from "components/toast";
import SelectInput from "components/forms/SelectInput";
import { useTableRefresh } from "context/TableRefresh";

const rfqStatusOptions = [
  { value: "0", label: "Estimated" },
  { value: "1", label: "New" },
  { value: "2", label: "Quoted" },
  { value: "3", label: "Accepted" },
  { value: "4", label: "Closed" },
  { value: "5", label: "Cancelled" },
  { value: "6", label: "Expired" },
];

const rfqStatusFilterOptions = [
  { value: "", label: "All statuses" },
  ...rfqStatusOptions,
];

const QuoteRequests = () => {
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [selectedRFQId, setSelectedRFQId] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [reopenModalOpen, setReopenModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const columnHelper = createColumnHelper();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);
  const toast = useToast();
  const { refreshKey } = useTableRefresh();
  const hasTrackedView = useRef(false);
  const [currentQuery, setCurrentQuery] = useState<any>(null);

  // Entry to RFQ's > Duplicates (staff only), with the count of extra RFQ's.
  const isStaff = user?.accessLevel === "isCompany";
  const [dupExtra, setDupExtra] = useState<number | null>(null);
  useEffect(() => {
    if (!isStaff) return;
    getRfqDuplicates().then((res: any) => setDupExtra(res?.error ? null : res?.extra ?? 0));
  }, [isStaff, refreshKey]);

  const tableFilters = [
    {
      key: "status",
      component: SelectInput,
      condition: "in",
      props: {
        name: "status",
        label: "Status",
        options: rfqStatusFilterOptions,
        clearable: true,
        searchable: false,
        multiple: true,
        $customStyles: { minWidth: "220px" },
      },
      convertValue: (value: any) => {
        if (Array.isArray(value)) {
          if (value.every((v: any) => typeof v === "object" && v !== null && "value" in v)) return value;
          return value.map((v: any) => rfqStatusFilterOptions.find((o) => o.value === `${v}`)).filter(Boolean);
        }
        if (!value || value === "") return [];
        return `${value}`.split(",").map((v: string) => rfqStatusFilterOptions.find((o) => o.value === v.trim())).filter(Boolean);
      },
      updateValue: (val: any) => {
        if (!val) return null;
        const values = Array.isArray(val) ? val : [val];
        const codes = values.map((v: any) => (typeof v === "object" && "value" in v ? v.value : `${v}`)).filter(Boolean);
        return codes.length ? codes.join(",") : null;
      },
    },
  ];

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | RFQ's";
    
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    
    void trackActivity({
      module: "quote-requests",
      action: "view_quote_requests_list",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: {
        page: currentQuery?.pageNum || 1,
        limit: currentQuery?.recordsPer || 15,
        filters: currentQuery?.filters || {},
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user, currentQuery]);

  const confirmReopenRFQ = (id: string) => {
    setSelectedRFQId(id);
    setReopenModalOpen(true);
  };

  const submitReopenRFQ = async () => {
    if (!selectedRFQId) return;
    setSubmitting(true);
    const res = await reviveRfq(selectedRFQId);
    if (res.success) {
      setRefreshData(true);
      setTimeout(() => setRefreshData(false), 100);
      setReopenModalOpen(false);
      toast.add("RFQ reopened.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res.message || "Something went wrong.", "var(--theme-ui-colors-danger)");
      setReopenModalOpen(false);
    }
    setSubmitting(false);
  };

  const confirmCancelRFQ = (id: string) => {
    setSelectedRFQId(id);
    setConfirmModalOpen(true);
  };

  useEffect(() => {
    setRefreshData(true);
    const timer = setTimeout(() => setRefreshData(false), 200); // give it a bit more time
    return () => clearTimeout(timer); // cleanup
  }, [refreshKey]);

  const submitCancelRFQ = async () => {
    if (!selectedRFQId) return;
    setSubmitting(true);
    
    const res = await updateQuoteRequest({ data: { id: selectedRFQId }, update: { status: 5 } });

    if (res?.id) {
      setRefreshData(true);
      setTimeout(() => setRefreshData(false), 100);
      setConfirmModalOpen(false);
      toast.add("RFQ cancelled.", "var(--theme-ui-colors-success)");
    } else {
      toast.add("Something went wrong.", "var(--theme-ui-colors-danger)");
      setConfirmModalOpen(false);
    }
    
    setSubmitting(false);
  };

  const cancelDeleteRFQ = async () => {
    setConfirmModalOpen(false);
    setSubmitting(false);
  };

  const columns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{fontWeight:500, cursor:"pointer", color:"#000"}} onClick={() => navigate(`/quote-requests/${info.row.original.id}`)}>
          {`GMR-${String(info.getValue()).padStart(5, "0")}`}
        </Box>
      ),
      header: () => <span>RFQ #</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.projectName ?? "Instant Estimate", {
      id: "projectName",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">
          {info.getValue()}
        </Span>
      ),
      header: () => <span>Name</span>,
      meta: {
        style: {  maxWidth: "200px" },
      },
    }),
    ...(user.role !== null
      ? [
          columnHelper.accessor((row: any) => row?.client?.name, {
            id: "client.name",
            cell: (info: any) => info.getValue(),
            header: () => <span>Company</span>,
          }),
        ]
      : []),
      columnHelper.accessor((row: any) => row?.primaryClient?.name, {
      id: "primaryClient.name",
      cell: (info: any) => info.getValue(),
      header: () => <span>{user.role !== null ? 'Primary Client' : 'Primary Contact'}</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        const row = info.row.original;
        const expiresAt = row?.expiresAt ? new Date(row.expiresAt) : null;
        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const isExpiringSoon = expiresAt && expiresAt > now && expiresAt <= sevenDaysFromNow && [0, 1].includes(row?.status);

        const badge = (() => {
          switch (info.getValue()) {
          case 1:
            return (
              <Badge as="span" variant="warning">
                New
              </Badge>
            );
          case 2:
            return (
              <Badge as="span" variant="success">
                Quoted
              </Badge>
            );
          case 5:
            return (
              <Badge as="span" variant="danger">
                Cancelled
              </Badge>
            );
          case 6:
            return (
              <Badge as="span" variant="orange">
                Expired
              </Badge>
            );
          default:
            return (
              <Badge as="span" variant="info">
                Estimated
              </Badge>
            );
        }
        })();

        return (
          <Flex sx={{ alignItems: "center", gap: "6px" }}>
            {isExpiringSoon && (
              <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#FF7F07", flexShrink: 0 }} title={`Expires ${expiresAt?.toLocaleDateString()}`} />
            )}
            {badge}
          </Flex>
        );
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.expiresAt || row?.expiredAt, {
      id: "expiresAt",
      cell: (info: any) => {
        const row = info.row.original;
        const expiresAt = row?.expiresAt;
        const expiredAt = row?.expiredAt;
        if (row?.status === 6 && expiredAt) {
          return <Span color="var(--theme-ui-colors-danger)" fontWeight="500">{moment(expiredAt).format("MMM D, YYYY")}</Span>;
        }
        if (expiresAt && [0, 1].includes(row?.status)) {
          const isExpiringSoon = new Date(expiresAt) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
          return <Span color={isExpiringSoon ? "#FF7F07" : themevals.colors.dkgrey} fontWeight="500">{moment(expiresAt).format("MMM D, YYYY")}</Span>;
        }
        return <Span color={themevals.colors.dkgrey}>{"\u2014"}</Span>;
      },
      header: () => <span>Expires</span>,
      meta: {
        style: { width: "120px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.estimate?.services[0]?.quantity, {
      id: "bulkQuantity",
      cell: (info: any) => {
        const quantity = info.getValue();
        const unit = info.row.original?.estimate?.product?.unit;
        return quantity !== undefined ? `${quantity} ${unit || ""}`.trim() : "";
      },
      header: () => <span>Product Qty.</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.serviceDate, {
      id: "serviceDate",
      // Calendar date: shown as picked (helpers/dateOnly), not shifted a day by the browser zone.
      cell: (info: any) => formatDateOnly(info.getValue()),
      header: () => <span>Estimated Service Date</span>,
    }),
    columnHelper.accessor((row: any) => row?.updatedAt, {
      id: "updatedAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Last Updated</span>,
    }),
    columnHelper.accessor((row: any) => `${row?.lastUpdateBy?.firstName || ""} ${row?.lastUpdateBy?.lastName || ""}`.trim(), {
      id: "updateBy",
      cell: (info: any) => info.getValue(),
      header: () => <span>Updated By</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const items: { label: string; onClick: () => void; style?: React.CSSProperties }[] = [{ label: "Details", onClick: () => navigate(`/quote-requests/${info.row.original.id}`) }];

        if ([0, 1].includes(info.row.original.status) && user.role !== null) {
          items.push({ label: "Edit", onClick: () => navigate(`/quote-requests/${info.row.original.id}/edit`) });
        }


        if ((info.row.original.status === 1 || info.row.original.status === 0) && user.role !== null) {
          items.push({ label: "Cancel", onClick: () => confirmCancelRFQ(info.row.original.id), style: { color: "var(--theme-ui-colors-danger)" } });
        }

        if (info.row.original.status === 6) {
          items.push({ label: "Reopen", onClick: () => confirmReopenRFQ(info.row.original.id) });
        }

      
        return (
          <Box>
            <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  return (
    <>
      <Header></Header>
      <Page
        title="RFQ's"
        subtitle={`Manage quote requests for your company. ${"RFQ's"} are generated from Instant Estimates.`}
        titleAction={isStaff && dupExtra ? (
          <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate("/quote-requests/duplicates")}>
            {`Duplicates \u00b7 ${dupExtra}`}
          </BasicButton>
        ) : undefined}
      >
        <TableData api={getQuoteRequests} columns={columns} refresh={refreshData} title={"All RFQ's"} defaultSortBy="updatedAt" defaultSortDir="desc" nothingFoundText="No Quote Requests found." filters={tableFilters} returnQuery={(query) => setCurrentQuery(query)} />
      </Page>
      {confirmModalOpen && (
        <BasicModal
          title="Cancel RFQ?"
          cancel_click={() => cancelDeleteRFQ()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={submitCancelRFQ}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Yes, cancel RFQ"}
            </BasicButton>
          }
        >
          <P>Are you sure you want to cancel this RFQ?</P>
        </BasicModal>
      )}
      {reopenModalOpen && (
        <BasicModal
          title="Reopen RFQ?"
          cancel_click={() => { setReopenModalOpen(false); setSubmitting(false); }}
          cancel_text="Cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={submitReopenRFQ}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {"Yes, reopen"}
            </BasicButton>
          }
        >
          <P>This will reopen the expired RFQ with a fresh expiration window and update estimate pricing to current rates.</P>
        </BasicModal>
      )}
    </>
  );
};

export default QuoteRequests;
